import { Phone, MessageCircle, MapPin, BadgeCheck, Truck, QrCode } from "lucide-react";

const VAN_IMAGE = "/img/van1.webp";

/**
 * Brand showcase section featuring the real GoVan.ie Ford Transit van wrap.
 * Positions the business as authentic, locally-owned, and identifiable on Dublin roads.
 */
export default function OurVanSection() {
  return (
    <section data-testid="our-van-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-center">

          {/* === Left: copy === */}
          <div className="lg:col-span-2">
            <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-[#0055FF] mb-3 block">Our Van</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A0A0A] tracking-tight mb-5">
              The Van You'll See at Your Door
            </h2>
            <p className="text-[#475569] text-base md:text-lg leading-relaxed mb-8">
              Our fully-branded white Ford Transit covers every postcode in Dublin. Look for the blue lettering, the phone number on the side, and a friendly local owner who actually answers the phone.
            </p>

            {/* Brand markers */}
            <div className="space-y-4 mb-8">
              {[
                { icon: Truck, title: "Ford Transit LWB", desc: "Plenty of space for full house moves, IKEA hauls and large furniture." },
                { icon: BadgeCheck, title: "Distinct Blue Branding", desc: "Easy to spot — \"Man with a Van\" in blue lettering with our phone number." },
                { icon: MapPin, title: "Local Dublin Plates", desc: "Independently owned and operated from Dublin. No franchise, no call centre." },
                { icon: QrCode, title: "QR Code on the Van", desc: "Scan it on the road for an instant free quote — straight to WhatsApp." },
              ].map((item) => (
                <div key={item.title} className="flex gap-4">
                  <div className="w-10 h-10 bg-[#0055FF]/10 flex items-center justify-center rounded-sm shrink-0 mt-0.5">
                    <item.icon className="w-5 h-5 text-[#0055FF]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-bold text-base text-[#0A0A0A] mb-1">{item.title}</h3>
                    <p className="text-[#475569] text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="tel:+353874199541" data-testid="our-van-cta-call" className="inline-flex">
                <button className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC] text-white text-base font-bold px-6 py-3.5 inline-flex items-center gap-2 transition-colors">
                  <Phone className="w-4 h-4" /> 087 419 9541
                </button>
              </a>
              <a
                href="https://wa.me/353874199541?text=Hi%20GoVan!%20I%20saw%20your%20van."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="our-van-cta-whatsapp"
                className="inline-flex"
              >
                <button className="rounded-sm bg-[#25D366] hover:bg-[#1fb855] text-white text-base font-bold px-6 py-3.5 inline-flex items-center gap-2 transition-colors">
                  <MessageCircle className="w-4 h-4" /> WhatsApp Us
                </button>
              </a>
            </div>
          </div>

          {/* === Right: van mockup === */}
          <div className="lg:col-span-3">
            <div className="relative bg-[#F8F9FA] border border-black/10 rounded-sm overflow-hidden">
              {/* Small label on top */}
              <div className="absolute top-4 left-4 z-10 bg-white border border-black/10 px-3 py-1.5 rounded-sm flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[#25D366]" />
                <span className="text-[10px] font-extrabold tracking-[0.15em] uppercase text-[#0A0A0A]">Real Dublin Branding</span>
              </div>

              <img
                src={VAN_IMAGE}
                alt="GoVans.ie branded white Ford Transit van — Man with a Van Dublin — 087 419 9541"
                width="960"
                height="1280"
                className="w-full h-auto block"
                loading="lazy"
                data-testid="our-van-image"
              />

              {/* Caption bar */}
              <div className="bg-[#0B1E36] px-5 py-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-white font-extrabold text-sm">GoVans.ie · Dublin Removals</p>
                  <p className="text-white/60 text-xs">Front · Side · Rear views of our branded fleet</p>
                </div>
                <span className="text-[#0055FF] font-extrabold text-base tracking-wide">087 419 9541</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
